import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bus, 
  Search, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Building2, 
  HelpCircle, 
  ChevronDown, 
  Layers,
  ExternalLink
} from 'lucide-react';
import SEO from '../components/SEO';
import BusDisclaimer from '../components/BusDisclaimer';
import rawData from '../data/busTimeTableData.json';
import { ENQUIRY_NUMBER } from '../config/busConfig';
import { SITE_URL } from '../config/seo';

export default function BusTimeTableThroughHub() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPair, setSelectedPair] = useState('ALL');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Filter 171 Through Buses passing Fatehabad
  const throughBuses = useMemo(() => {
    return rawData.timetable.filter(
      (r) => r.bus_stand === 'Fatehabad' && r.from !== 'Fatehabad'
    ).sort((a, b) => a.departure_time.localeCompare(b.departure_time));
  }, []);

  // 6 Primary Dedicated Routes
  const featuredRoutes = [
    { slug: 'sirsa-to-delhi', from: 'Sirsa', to: 'Delhi', count: 36, first: '03:40', last: '17:40' },
    { slug: 'delhi-to-sirsa', from: 'Delhi', to: 'Sirsa', count: 33, first: '01:30', last: '22:00' },
    { slug: 'sirsa-to-gurugram', from: 'Sirsa', to: 'Gurugram', count: 14, first: '06:00', last: '15:55' },
    { slug: 'sirsa-to-panipat', from: 'Sirsa', to: 'Panipat', count: 8, first: '07:40', last: '13:30' },
    { slug: 'hisar-to-sirsa', from: 'Hisar', to: 'Sirsa', count: 7, first: '08:10', last: '15:35' },
    { slug: 'rohtak-to-sirsa', from: 'Rohtak', to: 'Sirsa', count: 5, first: '09:00', last: '20:40' }
  ];

  // Distinct pairs
  const pairCounts = useMemo(() => {
    const map = {};
    throughBuses.forEach((b) => {
      const key = `${b.from} -> ${b.to}`;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [throughBuses]);

  const filteredBuses = useMemo(() => {
    return throughBuses.filter((b) => {
      const pairKey = `${b.from} -> ${b.to}`;
      if (selectedPair !== 'ALL' && pairKey !== selectedPair) return false;

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchTo = b.to.toLowerCase().includes(q);
        const matchFrom = b.from.toLowerCase().includes(q);
        const matchVia = b.via.toLowerCase().includes(q);
        const matchTime = b.departure_time.toLowerCase().includes(q);
        const matchOp = b.operator.toLowerCase().includes(q);
        if (!matchTo && !matchFrom && !matchVia && !matchTime && !matchOp) return false;
      }
      return true;
    });
  }, [throughBuses, selectedPair, searchTerm]);

  const pageTitle = 'Through Buses via Fatehabad Time Table 2026 - Roadways';
  const metaDesc = 'Through buses via Fatehabad bus stand time table 2026: 171 daily inter-state buses. Check official departure timings, route stops & bus stand enquiry contact.';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Bus Time Table',
        item: `${SITE_URL}/bus-time-table/`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Through Buses via Fatehabad',
        item: `${SITE_URL}/bus-time-table/through-buses/`
      }
    ]
  };

  const faqData = [
    {
      q: 'फतेहाबाद बस स्टैंड से कौन-कौन सी थ्रू (Through) बसें गुजरती हैं?',
      a: 'फतेहाबाद से मुख्य रूप से सिरसा-दिल्ली (36 बसें), दिल्ली-सिरसा (33 बसें), सिरसा-गुरुग्राम (14 बसें), सिरसा-पानीपत (8 बसें), हिसार-सिरसा, रोहतक-सिरसा सहित राजस्थान, पंजाब और दिल्ली की 171 बसें गुजरती हैं।'
    },
    {
      q: 'सिरसा से दिल्ली जाने वाली बसें फतेहाबाद कब पहुंचती हैं?',
      a: 'सिरसा से दिल्ली जाने वाली बसें फतेहाबाद बस स्टैंड पर सुबह 03:40 बजे से लेकर शाम 17:40 बजे तक लगातार हर 20-30 मिनट पर उपलब्ध रहती हैं।'
    },
    {
      q: 'क्या थ्रू बसों में फतेहाबाद से सीट मिल जाती है?',
      a: 'हाँ, हरियाणा रोडवेज और अधिकृत बसों में फतेहाबाद से यात्रियों के लिए पर्याप्त सीटें उपलब्ध रहती हैं। यात्री पूछताछ काउंटर से प्लेटफॉर्म जानकारी ले सकते हैं।'
    }
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a
      }
    }))
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: metaDesc,
    url: `${SITE_URL}/bus-time-table/through-buses/`,
    datePublished: '2026-10-18',
    dateModified: '2026-10-18',
    inLanguage: ['hi', 'en']
  };

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-gray-950 text-gray-800 dark:text-gray-200">
      <SEO
        title={pageTitle}
        titleTemplate=""
        description={metaDesc}
        canonical="/bus-time-table/through-buses/"
        jsonLd={[breadcrumbJsonLd, faqJsonLd, webPageJsonLd]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <ol className="flex items-center gap-1.5 sm:gap-2">
            <li>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/bus-time-table" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Bus Time Table
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-gray-900 dark:text-gray-100">
              Through Buses via Fatehabad
            </li>
          </ol>
        </nav>

        <div className="bg-gradient-to-br from-purple-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 text-purple-200 text-xs font-semibold uppercase tracking-wider border border-purple-400/30">
              <Layers className="h-3.5 w-3.5" />
              Inter-State & Regional Passing Buses
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Through Buses Passing via Fatehabad Time Table 2026 (फतेहाबाद से गुजरने वाली बसें)
            </h1>
            <p className="text-sm sm:text-base text-purple-100/90 leading-relaxed">
              फतेहाबाद बस स्टैंड से होकर गुजरने वाली सभी 171 इंटर-स्टेट व रीजनल बसों की विस्तृत समय सारिणी। सिरसा-दिल्ली, दिल्ली-सिरसा, गुरुग्राम, पानीपत, रोहतक, जयपुर और गंगानगर रूट की बसों का प्रस्थान समय देखें।
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2 text-xs sm:text-sm">
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🚌 171 Through Buses
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🏙️ 53 City Pairs
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🏛️ Delhi / Gurugram / Panipat Express
              </span>
            </div>
          </div>
        </div>

        <BusDisclaimer />

        {/* Featured High-Frequency Passing Routes */}
        <section aria-labelledby="featured-passing-heading" className="space-y-4">
          <h2 id="featured-passing-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            High-Frequency Inter-State Passing Routes (मुख्य थ्रू रूट)
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredRoutes.map((fr) => (
              <Link
                key={fr.slug}
                to={`/bus-time-table/${fr.slug}`}
                className="group bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 hover:border-purple-500 dark:hover:border-purple-500 transition shadow-sm hover:shadow"
              >
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                  <span className="font-bold text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/60 px-2.5 py-0.5 rounded">
                    {fr.count} Buses Daily
                  </span>
                  <span className="font-mono text-gray-600 dark:text-gray-300">
                    {fr.first} - {fr.last}
                  </span>
                </div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition">
                  {fr.from} to {fr.to}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Stops at Fatehabad Bus Stand
                </p>
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs font-semibold text-purple-600 dark:text-purple-400">
                  <span>Dedicated Timetable Page</span>
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* All Passing Buses Table */}
        <section aria-labelledby="passing-table-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 id="passing-table-heading" className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-purple-600" />
                  All 171 Passing Buses Schedule (सभी थ्रू बसें)
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Showing {filteredBuses.length} of {throughBuses.length} buses passing via Fatehabad Stand
                </p>
              </div>

              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search city, operator, time..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Quick Filter by Top Pairs */}
            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-800">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">
                Filter Route:
              </span>
              <button
                type="button"
                onClick={() => setSelectedPair('ALL')}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
                  selectedPair === 'ALL'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                All ({throughBuses.length})
              </button>
              {pairCounts.slice(0, 6).map(([pair, count]) => (
                <button
                  key={pair}
                  type="button"
                  onClick={() => setSelectedPair(selectedPair === pair ? 'ALL' : pair)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                    selectedPair === pair
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-gray-600 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {pair} ({count})
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <caption className="sr-only">Schedule of through buses passing Fatehabad bus stand</caption>
              <thead className="sticky top-0 bg-slate-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
                <tr className="text-gray-600 dark:text-gray-300 font-semibold">
                  <th scope="col" className="py-3 px-4 sm:px-6 w-14 text-center">#</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Departure Time</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">From</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">To</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Via Route</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Type</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Operator</th>
                  <th scope="col" className="py-3 px-4 sm:px-6 text-center">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredBuses.map((bus, idx) => (
                  <tr key={`${bus.serial_no}-${idx}`} className="hover:bg-purple-50/30 dark:hover:bg-purple-950/20 transition-colors">
                    <td className="py-2.5 px-4 sm:px-6 text-center font-mono text-gray-400">
                      {idx + 1}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-mono font-bold text-sm text-gray-900 dark:text-white">
                      <span className="inline-flex items-center gap-1 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50 px-2 py-0.5 rounded">
                        <Clock className="h-3 w-3" />
                        {bus.departure_time}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-medium text-gray-900 dark:text-white">
                      {bus.from}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-semibold text-gray-900 dark:text-white">
                      {bus.to}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 text-gray-600 dark:text-gray-400">
                      {bus.via}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300">
                        {bus.service_type}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-bold text-xs">
                      {bus.operator}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 text-center text-xs text-gray-500">
                      {bus.service_days || 'All Days'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQs */}
        <section aria-labelledby="through-faqs-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-4">
            <HelpCircle className="h-6 w-6 text-purple-600" />
            <h2 id="through-faqs-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Through Buses FAQs (अक्सर पूछे जाने वाले सवाल)
            </h2>
          </div>

          <div className="space-y-3">
            {faqData.map((f, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-900 dark:text-white hover:bg-slate-50 dark:hover:bg-gray-800/50 transition gap-3"
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180 text-purple-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/30">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
